import type {
    FilterCondition,
    AggregatedDataPoint,
    AggregatedReportResult,
    ReportConfiguration,
    MetricDefinition,
} from '../types/reportTypes';

// ─── Helper: Date Grouping ──────────────────────────────────────────────────

/**
 * Derive a group key from a date string based on the time sub-option.
 * Expects groupByField values like "date:daily", "date:weekly", etc.
 */
function getDateGroupKey(dateStr: string, timeOption: string): string {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Invalid Date';

    switch (timeOption) {
        case 'daily':
            return d.toISOString().slice(0, 10); // YYYY-MM-DD
        case 'weekly': {
            // ISO week: find Monday of the week
            const day = d.getDay();
            const diff = d.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(d);
            monday.setDate(diff);
            return `Week of ${monday.toISOString().slice(0, 10)}`;
        }
        case 'monthly':
            return d.toISOString().slice(0, 7); // YYYY-MM
        case 'quarterly': {
            const quarter = Math.ceil((d.getMonth() + 1) / 3);
            return `${d.getFullYear()}-Q${quarter}`;
        }
        case 'yearly':
            return `${d.getFullYear()}`;
        default:
            return d.toISOString().slice(0, 10);
    }
}

// ─── Helper: Aggregation Computation ────────────────────────────────────────

function computeAggregation(
    values: number[],
    aggregation: 'sum' | 'average' | 'count' | 'min' | 'max'
): number {
    if (values.length === 0) return 0;

    switch (aggregation) {
        case 'sum':
            return values.reduce((acc, v) => acc + v, 0);
        case 'average':
            return values.reduce((acc, v) => acc + v, 0) / values.length;
        case 'count':
            return values.length;
        case 'min':
            return Math.min(...values);
        case 'max':
            return Math.max(...values);
        default:
            return 0;
    }
}

// ─── aggregateReportData ────────────────────────────────────────────────────

/**
 * Group raw records by a dimension field and apply an aggregation function
 * to a metric field. Returns an array of AggregatedDataPoint.
 *
 * Pure function — no side effects, no API calls, no state mutations.
 */
export function aggregateReportData(
    records: Record<string, any>[],
    config: {
        metricField: string;
        groupByField: string;
        aggregation: 'sum' | 'average' | 'count' | 'min' | 'max';
        dateRangeStart: string;
        dateRangeEnd: string;
    }
): AggregatedDataPoint[] {
    if (records.length === 0) return [];

    // Filter records within the date range
    const filtered = records.filter((r) => {
        const createdAt = r.createdAt as string | undefined;
        if (!createdAt) return true;
        return createdAt >= config.dateRangeStart && createdAt <= config.dateRangeEnd;
    });

    // Determine grouping strategy
    const isDateGrouping = config.groupByField.startsWith('date:');
    const isNoneGrouping = config.groupByField === 'none';

    // Group records
    const groups = new Map<string, Record<string, any>[]>();

    for (const record of filtered) {
        let groupKey: string;

        if (isNoneGrouping) {
            groupKey = 'All';
        } else if (isDateGrouping) {
            const timeOption = config.groupByField.split(':')[1] || 'monthly';
            const dateValue = record.createdAt as string;
            groupKey = dateValue ? getDateGroupKey(dateValue, timeOption) : 'Unknown';
        } else {
            const rawValue = record[config.groupByField];
            groupKey = rawValue != null ? String(rawValue) : 'Unknown';
        }

        if (!groups.has(groupKey)) {
            groups.set(groupKey, []);
        }
        groups.get(groupKey)!.push(record);
    }

    // Aggregate each group
    const result: AggregatedDataPoint[] = [];

    for (const [groupKey, groupRecords] of groups) {
        const values = groupRecords
            .map((r) => {
                const val = r[config.metricField];
                return typeof val === 'number' ? val : parseFloat(val);
            })
            .filter((v) => !isNaN(v));

        result.push({
            groupKey,
            groupLabel: groupKey,
            value: computeAggregation(values, config.aggregation),
            count: groupRecords.length,
        });
    }

    return result;
}

// ─── aggregateWithComparison ────────────────────────────────────────────────

/**
 * Split records into primary and comparison date ranges, aggregate each
 * independently, and return an AggregatedReportResult with both datasets.
 *
 * Pure function — no side effects, no API calls, no state mutations.
 */
export function aggregateWithComparison(
    records: Record<string, any>[],
    config: ReportConfiguration,
    metricDef: MetricDefinition
): AggregatedReportResult {
    const aggConfig = {
        metricField: metricDef.field,
        groupByField: config.groupByField,
        aggregation: metricDef.aggregation,
        dateRangeStart: config.dateRangeStart,
        dateRangeEnd: config.dateRangeEnd,
    };

    const primary = aggregateReportData(records, aggConfig);
    const primaryTotal = primary.reduce((sum, dp) => sum + dp.value, 0);

    const result: AggregatedReportResult = {
        primary,
        total: primaryTotal,
    };

    // If comparison date range is provided, compute comparison dataset
    if (config.compareDateRangeStart && config.compareDateRangeEnd) {
        const comparisonConfig = {
            ...aggConfig,
            dateRangeStart: config.compareDateRangeStart,
            dateRangeEnd: config.compareDateRangeEnd,
        };

        const comparison = aggregateReportData(records, comparisonConfig);
        const comparisonTotal = comparison.reduce((sum, dp) => sum + dp.value, 0);

        result.comparison = comparison;
        result.comparisonTotal = comparisonTotal;
    }

    return result;
}

// ─── getOperatorFn ──────────────────────────────────────────────────────────

/**
 * Return a comparison function for the given operator and field type.
 *
 * Operators by field type:
 * - Text/Dropdown: Equals, Does Not Equal, In, Not In, Contains
 * - Numeric: Equals, Greater Than, Less Than, Between, ≥, ≤
 * - Date: On, Before, After, Between, In Last X Days
 *
 * Pure function — no side effects.
 */
export function getOperatorFn(
    operator: string,
    fieldType: string
): (fieldValue: any, filterValue: any) => boolean {
    // Normalize operator for comparison
    const op = operator.toLowerCase().trim();

    // ─── Text / Dropdown operators ───
    if (fieldType === 'text' || fieldType === 'dropdown' || fieldType === 'multi-select') {
        switch (op) {
            case 'equals':
                return (fv, val) => String(fv).toLowerCase() === String(val).toLowerCase();
            case 'does not equal':
                return (fv, val) => String(fv).toLowerCase() !== String(val).toLowerCase();
            case 'in':
                return (fv, val) => {
                    const list = Array.isArray(val) ? val : [val];
                    return list.some((v: any) => String(fv).toLowerCase() === String(v).toLowerCase());
                };
            case 'not in':
                return (fv, val) => {
                    const list = Array.isArray(val) ? val : [val];
                    return !list.some((v: any) => String(fv).toLowerCase() === String(v).toLowerCase());
                };
            case 'contains':
                return (fv, val) => String(fv).toLowerCase().includes(String(val).toLowerCase());
            default:
                return () => true;
        }
    }

    // ─── Numeric operators ───
    if (fieldType === 'number') {
        switch (op) {
            case 'equals':
                return (fv, val) => Number(fv) === Number(val);
            case 'greater than':
                return (fv, val) => Number(fv) > Number(val);
            case 'less than':
                return (fv, val) => Number(fv) < Number(val);
            case 'between':
                return (fv, val) => {
                    const [min, max] = Array.isArray(val) ? val : [val, val];
                    return Number(fv) >= Number(min) && Number(fv) <= Number(max);
                };
            case '≥':
            case 'greater than or equal to':
                return (fv, val) => Number(fv) >= Number(val);
            case '≤':
            case 'less than or equal to':
                return (fv, val) => Number(fv) <= Number(val);
            default:
                return () => true;
        }
    }

    // ─── Date operators ───
    if (fieldType === 'date') {
        switch (op) {
            case 'on':
                return (fv, val) => {
                    const fieldDate = new Date(fv).toISOString().slice(0, 10);
                    const filterDate = new Date(val as string).toISOString().slice(0, 10);
                    return fieldDate === filterDate;
                };
            case 'before':
                return (fv, val) => new Date(fv).getTime() < new Date(val as string).getTime();
            case 'after':
                return (fv, val) => new Date(fv).getTime() > new Date(val as string).getTime();
            case 'between':
                return (fv, val) => {
                    const [start, end] = Array.isArray(val) ? val : [val, val];
                    const t = new Date(fv).getTime();
                    return t >= new Date(start).getTime() && t <= new Date(end).getTime();
                };
            case 'in last x days':
                return (fv, val) => {
                    const days = Number(val);
                    const cutoff = new Date();
                    cutoff.setDate(cutoff.getDate() - days);
                    return new Date(fv).getTime() >= cutoff.getTime();
                };
            default:
                return () => true;
        }
    }

    // ─── Boolean operators ───
    if (fieldType === 'boolean') {
        switch (op) {
            case 'equals':
                return (fv, val) => Boolean(fv) === (val === true || val === 'true');
            default:
                return () => true;
        }
    }

    // Fallback: always pass
    return () => true;
}

// ─── applyFilters ───────────────────────────────────────────────────────────

/**
 * Apply AND logic across all FilterCondition objects.
 * Returns only records that satisfy ALL conditions.
 *
 * Pure function — no side effects, no API calls, no state mutations.
 */
export function applyFilters(
    records: Record<string, any>[],
    filters: FilterCondition[]
): Record<string, any>[] {
    if (filters.length === 0) return records;

    // Pre-compute operator functions for each filter
    const filterFns = filters.map((filter) => ({
        fieldKey: filter.fieldKey,
        value: filter.value,
        fn: getOperatorFn(filter.operator, filter.fieldType),
    }));

    return records.filter((record) =>
        filterFns.every(({ fieldKey, value, fn }) => {
            const fieldValue = record[fieldKey];
            // If the field is missing/undefined, the record does not satisfy the condition
            if (fieldValue === undefined || fieldValue === null) return false;
            return fn(fieldValue, value);
        })
    );
}
